import {Component, OnDestroy, OnInit, Input} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, Validators} from '@angular/forms';
import * as fromStore from '../../_store';
import * as _ from 'lodash';
import {Store} from '@ngrx/store';
import {BehaviorSubject, combineLatest, Observable, Subscription} from 'rxjs';
import {INDRAPax} from '@ibe.models/pax.indra.model.lib';
import {ContactTypeEnum} from './enum/contact-type.enum';
import {take} from 'rxjs/operators';
import {SelectInputOption} from '@ibe.components/common/inputs/select-input-indra/models/select-input-option';
import * as fromGlobalStore from '@ibe/store/index';
import { Router } from '@angular/router';

@Component({
  selector: 'app-passenger-detail-contacts',
  templateUrl: './passenger-detail-contacts.component.html',
  styleUrls: ['./passenger-detail-contacts.component.scss']
})
export class PassengerDetailContactsComponent implements OnInit, OnDestroy {
	
	@Input() displayValidationMessages: boolean = false;
	
	public form: FormGroup = this.fb.group({
		email: [null, {
			validators: [Validators.required, Validators.email],
			updateOn: 'blur'
		}],
		phoneType: [null, Validators.required],
		phoneCountryCode: [null, Validators.required],
		phone: [null, [Validators.required, Validators.pattern('[0-9]+')]]
	});

	public isBookingPaymentPage: boolean = false;
	public displayMode: 'view' | 'edit';
	public contactTypes = ContactTypeEnum.allValues
		.map(ct => ({text: ContactTypeEnum.description(ct), value: ct} as SelectInputOption));
	public phoneCountryCodes = [];

	private sub$ = new Subscription();

	private _hasChanges: BehaviorSubject<boolean> = new BehaviorSubject(false);
	get hasChanges(): Observable<boolean> {
		return this._hasChanges.asObservable();
	}

	get hasData(): boolean {
		return !_.isEmpty(this.form.value);
	}

	get classViewOnlyForm(): string {
		return Object.is('view', this.displayMode) ? 'sidebar__form--view-only' : '';
	}

	get invalidatedClassEmail(): string {
		return this.form.get('email').invalid && this.form.touched && this.displayValidationMessages ? 'invalidated' : '';
	}

	get hasErrorClassPhoneType(): string {
		return this.form.get('phoneType').invalid && this.form.touched && this.displayValidationMessages ? 'has-error' : '';
	}

	get hasErrorPhoneCountryCode(): string {
		return this.form.get('phoneCountryCode').invalid && this.form.touched && this.displayValidationMessages ? 'has-error' : '';
	}

	get hasErrorPhone(): string {
		return this.form.get('phone').invalid && this.form.touched && this.displayValidationMessages ? 'has-error' : '';
	}

	constructor(
		private fb: FormBuilder,
		private store: Store<fromStore.State>,
		private globalStore: Store<fromGlobalStore.State>,
		private router: Router
	) { }

	ngOnInit() {
		
		if (this.router.url.includes('/booking/payment') || this.router.url.includes('/time-to-think/payment')) {
			this.isBookingPaymentPage = true;
		}

		this.sub$.add(
			this.globalStore.select(fromGlobalStore.getSelectedCountryCodesPrefixOpts)
				.subscribe(data => this.phoneCountryCodes = data)
		);

		this.sub$.add(
			this.store.select(fromStore.getSelectedPassengersAtIndex).subscribe((data: INDRAPax) => {
				this.form.patchValue({
					...data,
					phoneCountryCode: this.mapPhoneCountryCode(data.phoneCountryCode)
			}, {emitEvent: false});

				this.updateFormValidity();

				if (this.hasData && this.form.valid) {
					this.displayMode = this.isBookingPaymentPage ? 'edit' : 'view';
				} else {
					this.displayMode = 'edit';
				}
			})
		);

		this.sub$.add(
			this.form.valueChanges.subscribe(() => {
				this._hasChanges.next(true);
				this.store.dispatch(
					new fromStore.UpdateFormChangedAction({ 'updateContact': true })
				);
			})
		);

		// this.emailChangedUpdateValidations();
		// this.contactInfoChangedUpdateValidations();
		
		// dispatch by default since contact section is always required
		this.store.dispatch(new fromStore.UpdateFormChangedAction({ 'updateContact': true }));
	}

	private mapPhoneCountryCode(countryCode: string): string {
		if (!_.isNil(countryCode)) {
			return Object.is('+', countryCode.charAt(0)) ? countryCode : '+' + countryCode;
		}
		return null;
	}

	private emailChangedUpdateValidations() {
		const emailControl = this.form.get('email');
		const phoneTypeControl = this.form.get('phoneType');
		const phoneCountryCodeControl = this.form.get('phoneCountryCode');
		const phoneControl = this.form.get('phone');

		emailControl.valueChanges.subscribe(value => {
			if (!_.isEmpty(value) && emailControl.valid) {
				phoneTypeControl.clearValidators();
				phoneCountryCodeControl.clearValidators();
				phoneControl.clearValidators();
			} else {
				phoneTypeControl.setValidators(Validators.required);
				phoneCountryCodeControl.setValidators(Validators.required);
				phoneControl.setValidators(Validators.required);
			}
			phoneTypeControl.updateValueAndValidity({emitEvent: false});
			phoneCountryCodeControl.updateValueAndValidity({emitEvent: false});
			phoneControl.updateValueAndValidity({emitEvent: false});
		});
	}

	private contactInfoChangedUpdateValidations() {
		const emailControl = this.form.get('email');

		this.sub$.add(
			this.form.get('phoneCountryCode').valueChanges.subscribe(value => {
				const {phoneType, phone} = this.form.value;
				this.verifyEmailValidity(emailControl, value, phoneType, phone);
			})
		);

		this.sub$.add(
			this.form.get('phoneType').valueChanges.subscribe(value => {
				const {phoneCountryCode, phone} = this.form.value;
				this.verifyEmailValidity(emailControl, phoneCountryCode, value, phone);
			})
		);

		this.sub$.add(
			this.form.get('phone').valueChanges.subscribe(value => {
				const {phoneCountryCode, phoneType} = this.form.value;
				this.verifyEmailValidity(emailControl, phoneCountryCode, phoneType, value);
			})
		);
	}

	private verifyEmailValidity(emailControl: AbstractControl, phoneCountryCode?: string, phoneType?: string, phone?: string) {
		if (!_.isEmpty(phoneCountryCode) && !_.isEmpty(phoneType) && !_.isEmpty(phone)) {
			emailControl.setValidators([
				Validators.email
			]);
		} else {
			emailControl.setValidators([
				Validators.required,
				Validators.email
			]);
		}
		emailControl.updateValueAndValidity({emitEvent: false});
	}

	private updateFormValidity() {
		const {
			email,
			phoneCountryCode,
			phone,
			phoneType
		} = this.form.value;
		const isEmailValid = !_.isEmpty(email);
		const isContactValid = !_.isEmpty(phoneCountryCode) && !_.isEmpty(phoneType) && !_.isEmpty(phone);

		if (isEmailValid) {
			const phoneTypeControl = this.form.get('phoneType');
			const phoneCountryCodeControl = this.form.get('phoneCountryCode');
			const phoneControl = this.form.get('phone');
			phoneTypeControl.clearValidators();
			phoneCountryCodeControl.clearValidators();
			phoneControl.clearValidators();
			phoneTypeControl.updateValueAndValidity({emitEvent: false});
			phoneCountryCodeControl.updateValueAndValidity({emitEvent: false});
			phoneControl.updateValueAndValidity({emitEvent: false});
		}

		if (isContactValid) {
			const emailControl = this.form.get('email');
			emailControl.clearValidators();
			emailControl.updateValueAndValidity({emitEvent: false});
		}

	}

	ngOnDestroy() {
		this.sub$.unsubscribe();
	}

	public trackByFn(index: number, item: any): number {
		return index;
	}

	public formContactsErrors(control: string): string {
		const errors = [];
		if (Object.is('email', control)) {
			if (this.form.controls[control].hasError('required')) {
				errors.push('bookingDetails.error.paxContactEmailRequired');
			}
			if (this.form.controls[control].hasError('email')) {
				errors.push('bookingDetails.error.paxContactEmailInvalid');
			}
		}
		if (Object.is('phoneType', control)) {
			if (this.form.controls[control].hasError('required')) {
				errors.push('bookingDetails.error.paxContactPhoneNumTypeRequired');
			}
		}
		if (Object.is('phoneCountryCode', control)) {
			if (this.form.controls[control].hasError('required')) {
				errors.push('bookingDetails.error.paxContactCountryRequired');
			}
		}
		if (Object.is('phone', control)) {
			if (this.form.controls[control].hasError('required')) {
				errors.push('bookingDetails.error.paxContactPhoneNumRequired');
			}
			/*
			if (this.form.controls[control].hasError('pattern')) {
				errors.push('bookingDetails.error.paxContactPhoneNumPattern');
			}
			*/
		}
		return errors.join('; ');
	}

	public clearForm() {
		this.form.reset();
	}

	public cancelEditForm() {
		this.store.select(fromStore.getSelectedPassengersAtIndex)
			.pipe(take(1)).subscribe((data: INDRAPax) => {
				this.form.reset({
					...data,
					phoneCountryCode: this.mapPhoneCountryCode(data.phoneCountryCode)
				}, {emitEvent: false});
				this._hasChanges.next(false);
			});

		this.store.dispatch(
			new fromStore.UpdateFormChangedAction({ 'updateContact': false })
		);
		this.displayMode = 'view';
	}

	public openEditForm() {
		this.displayMode = 'edit';
	}

}
